// Curated exercise coaching keyed by canonical exercise identity.
// Returns { summary, setup, execution, cues, curated }.
//
// Specific movement rules are checked before broad fallbacks. If a movement is
// not recognized, the UI says so and gives conservative general guidance rather
// than presenting unrelated exercise-specific instructions.

import { normalizeExerciseName } from './exerciseIdentity';
import { getExerciseAlternativeFormGuide } from '../data/exerciseAlternativeGuides';

const guide = (summary, setup, execution, cues) => ({
  summary,
  setup,
  execution,
  cues,
  curated: true,
});

const FALLBACK = {
  summary: 'Technique guidance is unavailable for this movement.',
  setup: [],
  execution: [],
  cues: [],
  curated: false,
};

const GUIDES = {
  jumpingJack: guide(
    'Land softly and keep your knees tracking with your toes as the feet move out and back.',
    [
      'Stand tall with feet together or hip-width and arms relaxed at your sides.',
      'Keep a slight bend in the knees instead of locking them.',
      'Brace lightly through the trunk so the torso stays upright.',
    ],
    [
      'Jump the feet out while sweeping the arms out and overhead in one smooth motion.',
      'Land on the balls/midfoot with soft knees, then reverse the motion back to the start.',
      'Use a steady rhythm you can maintain without hard, noisy landings.',
    ],
    [
      'Think quiet feet.',
      'Keep knees pointed in the same direction as the toes.',
      'Scale to step-out jacks if jumping causes discomfort.',
    ],
  ),
  pushUp: guide(
    'Keep one straight line from head to heels and let the elbows travel roughly 30–45° from the torso.',
    [
      'Place hands slightly wider than shoulder width with fingers spread for a stable base.',
      'Set the body in a straight line from head through hips to heels.',
      'Brace the abs and squeeze the glutes before the first rep.',
    ],
    [
      'Lower the chest toward the floor while the whole body moves as one unit.',
      'Stop at the deepest position you can control without the shoulders rolling forward.',
      'Press the floor away until the elbows are straight, keeping the trunk rigid.',
    ],
    [
      'Do not let the hips sag or pike upward.',
      'Keep the neck neutral instead of reaching the chin toward the floor.',
      'Use an elevated surface if full floor reps cannot be controlled.',
    ],
  ),
  pullUp: guide(
    'Start the pull by setting the shoulder blades, then drive the elbows down instead of swinging for momentum.',
    [
      'Take a secure overhand grip and begin from a controlled hang.',
      'Keep ribs stacked over the pelvis instead of aggressively arching the lower back.',
      'Set the shoulders down away from the ears before bending the elbows hard.',
    ],
    [
      'Pull the elbows down toward the ribs as the chest rises toward the bar.',
      'Reach your highest controlled position without craning the neck.',
      'Lower under control to a long-arm position before the next repetition.',
    ],
    [
      'Avoid kicking, swinging, or kipping unless that variation is specifically programmed.',
      'Think elbows to your back pockets.',
      'Use band or machine assistance if full reps cannot be controlled.',
    ],
  ),
  chinUp: guide(
    'Use the underhand grip to pull the elbows down while keeping the torso quiet and the shoulders away from the ears.',
    [
      'Take a secure underhand grip around shoulder width.',
      'Begin from a controlled hang with the trunk braced.',
      'Set the shoulder blades before initiating the main pull.',
    ],
    [
      'Drive the elbows down as the chest rises toward the bar.',
      'Keep the wrists neutral instead of letting them fold backward.',
      'Lower slowly to a long-arm position before repeating.',
    ],
    [
      'Do not swing the legs to create momentum.',
      'Keep the shoulders from shrugging into the ears.',
      'Use assistance if you cannot control the lowering phase.',
    ],
  ),
  floorPress: guide(
    'Keep the forearms nearly vertical and let the upper arms touch the floor lightly—do not bounce the elbows.',
    [
      'Lie on your back with knees bent and feet planted securely.',
      'Hold the dumbbells over the chest with wrists stacked over elbows.',
      'Position the upper arms roughly 30–45° from the torso rather than flared straight out.',
    ],
    [
      'Press the dumbbells upward until the arms are straight without letting the shoulders roll forward.',
      'Lower under control until the backs of the upper arms lightly contact the floor.',
      'Pause briefly on the floor, maintain tension, then begin the next press.',
    ],
    [
      'Do not slam or bounce the elbows off the floor.',
      'Keep wrists over elbows throughout the rep.',
      'Keep the shoulder blades gently pulled back and down.',
    ],
  ),
  benchPress: guide(
    'Keep the shoulder blades set and the forearms vertical as the bar or dumbbells lower toward the chest.',
    [
      'Lie with eyes roughly under the bar, feet planted, and upper back firmly supported.',
      'Pull the shoulder blades back and down to create a stable pressing base.',
      'Choose a grip that lets the wrists stay stacked over the elbows near the bottom.',
    ],
    [
      'Lower with control toward the mid-to-lower chest while keeping the elbows below the wrists.',
      'Press up and slightly back toward the starting position.',
      'Finish with straight arms without letting the shoulders roll off the bench.',
    ],
    [
      'Keep wrists neutral rather than bent far backward.',
      'Do not bounce the weight off the chest.',
      'Use safeties or a spotter for challenging barbell sets.',
    ],
  ),
  chestFly: guide(
    'Keep a soft, nearly fixed elbow bend and move the arms in a wide hugging arc instead of turning the movement into a press.',
    [
      'Lie on a bench or floor with dumbbells above the chest and palms facing each other.',
      'Set the shoulder blades gently back and down.',
      'Keep a slight bend in the elbows and hold that angle throughout the rep.',
    ],
    [
      'Open the arms in a controlled arc until you reach a comfortable chest stretch.',
      'Reverse the same arc and bring the dumbbells back above the chest.',
      'If using the floor, let the floor naturally limit the bottom range.',
    ],
    [
      'Do not chase extra depth by letting the shoulders roll forward.',
      'Keep the elbows softly bent, not changing angle like a press.',
      'Use less weight than you would for a press.',
    ],
  ),
  dip: guide(
    'Keep the shoulders down and control the depth; press back up without bouncing out of the bottom.',
    [
      'Support yourself securely on parallel bars with arms straight and shoulders pulled away from the ears.',
      'Keep the torso mostly upright for greater triceps emphasis.',
      'Brace the trunk and keep the legs quiet.',
    ],
    [
      'Bend the elbows and lower only as far as the shoulders remain comfortable and controlled.',
      'Keep the elbows tracking mostly backward rather than flaring sharply outward.',
      'Press the bars down to return to straight arms.',
    ],
    [
      'Do not drop into the bottom position.',
      'Avoid excessive shoulder depth if it causes discomfort.',
      'Use assisted dips if bodyweight reps are not controlled.',
    ],
  ),
  benchDip: guide(
    'Keep the hips close to the bench and use a shallow, controlled range rather than forcing deep shoulder extension.',
    [
      'Place hands securely on the bench beside the hips and move the hips just off the edge.',
      'Keep shoulders down away from the ears.',
      'Bend the knees to make the movement easier; straighten the legs to make it harder.',
    ],
    [
      'Bend the elbows and lower only through a comfortable shoulder range.',
      'Keep the hips close to the bench instead of drifting forward.',
      'Press through the hands until the elbows are straight again.',
    ],
    [
      'Do not force a deep bottom position.',
      'Keep elbows pointing generally backward.',
      'Choose another triceps movement if the shoulder position is uncomfortable.',
    ],
  ),
  overheadTricepsExtension: guide(
    'Keep the upper arms pointed mostly upward while the elbows bend and straighten; do not turn it into a shoulder swing.',
    [
      'Stand or sit tall with the dumbbell held securely overhead.',
      'Brace the abs and keep the ribs from flaring upward.',
      'Point the elbows forward/upward and keep the upper arms close to the head.',
    ],
    [
      'Bend only at the elbows to lower the weight behind the head through a comfortable range.',
      'Extend the elbows to bring the weight back overhead.',
      'Keep the upper arms as still as practical throughout the repetition.',
    ],
    [
      'Do not overarch the lower back.',
      'Avoid letting the elbows flare excessively.',
      'Use a lighter load if the shoulders or elbows cannot stay controlled.',
    ],
  ),
  tricepsKickback: guide(
    'Lock the upper arm beside the torso and straighten the elbow without swinging the shoulder.',
    [
      'Hinge at the hips with a neutral spine and brace the torso.',
      'Bring the upper arm alongside the body with the elbow bent around 90°.',
      'Keep the shoulder and upper arm fixed before starting the rep.',
    ],
    [
      'Straighten the elbow until the arm is nearly in line with the torso.',
      'Pause briefly with the triceps contracted.',
      'Bend the elbow under control without letting the upper arm swing forward.',
    ],
    [
      'Only the forearm should do most of the moving.',
      'Do not use torso momentum to throw the dumbbell backward.',
      'Keep the neck in line with the spine.',
    ],
  ),
  tricepsPushdown: guide(
    'Pin the upper arms near your sides and extend only at the elbows instead of leaning bodyweight onto the cable.',
    [
      'Stand tall facing the cable with the elbows close to the ribs.',
      'Brace the trunk and keep the shoulders down.',
      'Start with the forearms flexed while the upper arms stay nearly vertical.',
    ],
    [
      'Press the attachment down by straightening the elbows.',
      'Pause briefly near full extension without aggressively locking the joints.',
      'Return under control until the forearms rise without the elbows drifting forward.',
    ],
    [
      'Keep the upper arms still.',
      'Do not rock the torso to move more weight.',
      'Keep wrists neutral.',
    ],
  ),
  overheadPress: guide(
    'Brace the ribs over the pelvis and press overhead without turning the lift into a standing backbend.',
    [
      'Stand or sit tall with the weight at shoulder level.',
      'Stack wrists over forearms and keep the elbows slightly in front of the body.',
      'Brace the glutes and abdomen before pressing.',
    ],
    [
      'Press the weight overhead while keeping it balanced over the midfoot or torso.',
      'Finish with the arms overhead without shrugging excessively.',
      'Lower under control back to shoulder level.',
    ],
    [
      'Keep the ribs down and avoid excessive lower-back arch.',
      'Do not flare the elbows straight out to the sides.',
      'Use a load that allows a smooth, controlled bar or dumbbell path.',
    ],
  ),
  lateralRaise: guide(
    'Raise the arms with a soft elbow bend and stop around shoulder height without shrugging the weight upward.',
    [
      'Stand tall with light dumbbells at your sides and elbows softly bent.',
      'Keep shoulders down and the trunk braced.',
      'Use a load light enough to avoid swinging.',
    ],
    [
      'Lift the arms out to the sides in a smooth arc.',
      'Stop around shoulder height or slightly below if that feels better.',
      'Lower slowly back to the starting position.',
    ],
    [
      'Lead with the elbows rather than flicking the wrists.',
      'Do not shrug the shoulders toward the ears.',
      'Avoid leaning or heaving to start the rep.',
    ],
  ),
  frontRaise: guide(
    'Lift the weight in front without leaning back; keep the shoulder blade controlled and use a light load.',
    [
      'Stand tall with the weight in front of the thighs.',
      'Keep a soft bend in the elbows and brace the trunk.',
      'Set the shoulders down away from the ears.',
    ],
    [
      'Raise the arms forward in a controlled arc toward shoulder height.',
      'Pause briefly without shrugging.',
      'Lower slowly to the thighs.',
    ],
    [
      'Do not lean backward to create momentum.',
      'Keep wrists neutral.',
      'Stop short of any painful shoulder range.',
    ],
  ),
  bicepsCurl: guide(
    'Keep the upper arms quiet beside the torso and curl by bending the elbows rather than swinging the body.',
    [
      'Stand tall with arms extended and elbows near the ribs.',
      'Brace the trunk and keep wrists neutral.',
      'Start with shoulders relaxed instead of rolled forward.',
    ],
    [
      'Curl the weight by flexing the elbows while the upper arms stay mostly still.',
      'Squeeze briefly near the top without pushing the elbows far forward.',
      'Lower slowly to full comfortable elbow extension.',
    ],
    [
      'Do not use hip or back swing to start the rep.',
      'Keep wrists from curling toward the forearms.',
      'Control the lowering phase.',
    ],
  ),
  hammerCurl: guide(
    'Keep a neutral thumb-up grip and pin the upper arms near the torso while the elbows flex.',
    [
      'Stand tall with the resistance at your sides and palms facing inward.',
      'Keep elbows close to the ribs and wrists straight.',
      'Brace the trunk before curling.',
    ],
    [
      'Curl the resistance while maintaining the neutral grip.',
      'Pause briefly near the top without moving the shoulders forward.',
      'Lower under control to straight arms.',
    ],
    [
      'Do not swing the torso.',
      'Keep the thumbs pointing generally upward.',
      'Let the elbow, not the shoulder, create the movement.',
    ],
  ),
  latPulldown: guide(
    'Pull the bar toward the upper chest by driving the elbows down; do not turn it into a behind-the-neck pull.',
    [
      'Sit securely with thighs held under the pads and take a comfortable overhand grip.',
      'Lift the chest slightly while keeping the ribs controlled.',
      'Set the shoulders down away from the ears before the main pull.',
    ],
    [
      'Drive the elbows down and back as the bar travels toward the upper chest.',
      'Pause briefly without leaning far backward.',
      'Return the bar overhead under control until the arms are long again.',
    ],
    [
      'Do not pull the bar behind the neck.',
      'Avoid turning the rep into a large torso swing.',
      'Think elbows toward your sides rather than hands toward your chest.',
    ],
  ),
  seatedRow: guide(
    'Keep the torso stable and pull the handle toward the lower ribs by moving the elbows back, not by rocking the whole body.',
    [
      'Sit tall with feet braced and a neutral spine.',
      'Begin with arms long and shoulders controlled, not collapsed forward.',
      'Brace the trunk before initiating the pull.',
    ],
    [
      'Drive the elbows back and pull the handle toward the lower ribs or upper abdomen.',
      'Pause briefly with the shoulder blades drawn together.',
      'Reach forward under control without rounding excessively through the lower back.',
    ],
    [
      'Avoid large forward/backward torso swings.',
      'Keep shoulders down away from the ears.',
      'Lead with the elbows rather than curling the handle with the arms.',
    ],
  ),
  bentOverRow: guide(
    'Hold a stable hip hinge and row toward the ribs without repeatedly standing up to move the weight.',
    [
      'Hinge at the hips with knees softly bent and the spine neutral.',
      'Brace the trunk and let the arms hang beneath the shoulders.',
      'Keep the weight close enough that you can maintain the hinge position.',
    ],
    [
      'Pull the elbows back toward the hips/ribs while the torso stays nearly fixed.',
      'Pause briefly at the top without shrugging.',
      'Lower until the arms are long again while keeping the same hip hinge.',
    ],
    [
      'Do not jerk the torso upright to finish the rep.',
      'Keep the lower back neutral.',
      'Lead with the elbows, not the hands.',
    ],
  ),
  facePull: guide(
    'Pull the rope toward eye/forehead level while separating the hands and keeping the shoulders down.',
    [
      'Set the cable around face height and take the rope with both hands.',
      'Step back into a stable stance with arms extended.',
      'Keep ribs down and shoulders relaxed away from the ears.',
    ],
    [
      'Pull the rope toward the face while driving the elbows outward/back.',
      'Separate the rope ends so the hands finish beside the face.',
      'Return slowly until the arms are extended again.',
    ],
    [
      'Do not shrug the shoulders upward.',
      'Use a light enough load to control the external-rotation finish.',
      'Keep the torso from rocking backward.',
    ],
  ),
  romanianDeadlift: guide(
    'Push the hips backward with only a soft knee bend and keep the weight close to the legs.',
    [
      'Stand tall with the weight in front of the thighs and feet around hip width.',
      'Soften the knees slightly, brace the trunk, and set the shoulders.',
      'Keep the weight close to the body before beginning the hinge.',
    ],
    [
      'Push the hips backward while the torso tips forward and the shins stay mostly vertical.',
      'Lower only until you reach your controlled hamstring range without rounding the back.',
      'Drive the hips forward to stand tall again.',
    ],
    [
      'This is a hip hinge, not a squat.',
      'Keep the dumbbells/bar close to the legs.',
      'Stop the descent when the pelvis or spine starts changing position.',
    ],
  ),
  deadlift: guide(
    'Brace before the pull, keep the load close, and push the floor away instead of yanking the weight from the ground.',
    [
      'Set the feet so the load is over the midfoot and take a secure grip.',
      'Bring the torso into position with a neutral spine and the shoulders slightly in front of or over the load.',
      'Brace the trunk before creating tension against the weight.',
    ],
    [
      'Push through the floor as the knees and hips extend together.',
      'Keep the bar or dumbbells close to the legs throughout the lift.',
      'Stand tall at the top, then reverse the movement under control.',
    ],
    [
      'Do not jerk a slack bar from the floor.',
      'Avoid rounding the lower back to reach the weight.',
      'Finish by standing tall, not by leaning backward.',
    ],
  ),
  squat: guide(
    'Keep the whole foot planted and let the knees track with the toes as the hips descend between the legs.',
    [
      'Set the feet around shoulder width with a comfortable slight toe-out angle.',
      'Brace the trunk and keep the whole foot in contact with the floor.',
      'For loaded variations, secure the weight before beginning the descent.',
    ],
    [
      'Bend the knees and hips together while allowing the knees to travel in the same direction as the toes.',
      'Descend only as deep as you can while maintaining balance and control.',
      'Drive through the whole foot to stand back up.',
    ],
    [
      'Do not let the knees collapse inward.',
      'Keep heels planted unless the specific variation intentionally uses heel elevation.',
      'Choose depth based on controlled mobility, not an arbitrary target.',
    ],
  ),
  lunge: guide(
    'Keep the front foot planted and lower mostly straight down rather than diving the torso forward.',
    [
      'Stand tall and step into a stance long enough for both legs to bend comfortably.',
      'Keep the front foot flat and the torso stacked over the pelvis.',
      'Brace lightly before lowering.',
    ],
    [
      'Lower under control until both knees are comfortably bent.',
      'Keep the front knee tracking in line with the toes.',
      'Push through the front foot to return or continue into the next walking step.',
    ],
    [
      'Do not let the front knee collapse inward.',
      'Keep enough stance width for balance instead of walking on a tightrope line.',
      'Shorten the range if balance or knee comfort deteriorates.',
    ],
  ),
  legPress: guide(
    'Keep the hips and lower back supported while the knees track with the toes; do not chase depth by curling the pelvis off the pad.',
    [
      'Place the feet securely on the platform at a comfortable width.',
      'Keep the hips, back, and head supported against the machine.',
      'Release the safeties only after the platform is controlled.',
    ],
    [
      'Lower the platform until you reach a controlled knee/hip bend without the pelvis rolling off the pad.',
      'Press through the whole foot as the knees extend.',
      'Stop just short of aggressively snapping the knees into lockout.',
    ],
    [
      'Keep knees tracking with the toes.',
      'Do not let the lower back round away from the pad at the bottom.',
      'Re-engage the safeties before exiting the machine.',
    ],
  ),
  legExtension: guide(
    'Keep the knee aligned with the machine pivot and extend smoothly without kicking the pad with momentum.',
    [
      'Adjust the seat so the knee joint lines up closely with the machine pivot.',
      'Place the shin pad above the ankle rather than on the foot.',
      'Sit back against the pad and hold the handles if provided.',
    ],
    [
      'Extend the knees smoothly until the legs are nearly straight.',
      'Pause briefly while keeping the hips against the seat.',
      'Lower the pad under control to the starting bend.',
    ],
    [
      'Do not swing the torso to move more weight.',
      'Avoid slamming into lockout.',
      'Use a range that feels comfortable at the knees.',
    ],
  ),
  legCurl: guide(
    'Keep the hips anchored while bending the knees; do not lift the pelvis to shorten the movement.',
    [
      'Adjust the machine so the knee lines up with the pivot and the pad sits above the heel/ankle.',
      'Keep the hips square and supported.',
      'Brace lightly before curling.',
    ],
    [
      'Bend the knees to draw the pad toward the body.',
      'Pause briefly at the strongest controlled contraction.',
      'Return slowly until the knees are extended through a comfortable range.',
    ],
    [
      'Keep the hips from lifting off the pad.',
      'Do not bounce the weight stack.',
      'Control the lowering phase.',
    ],
  ),
  hipThrust: guide(
    'Finish by squeezing the glutes with the ribs down; do not gain height by overextending the lower back.',
    [
      'Support the upper back on a stable bench and place the feet so the shins are near vertical at the top.',
      'Position and pad the load securely across the hips if using weight.',
      'Tuck the chin slightly and brace the trunk.',
    ],
    [
      'Drive through the feet and lift the hips until the torso and thighs form a strong line.',
      'Pause with the glutes contracted while keeping the ribs down.',
      'Lower the hips under control before repeating.',
    ],
    [
      'Do not finish by arching the lower back.',
      'Keep knees tracking with the toes.',
      'Adjust foot position if you feel the movement mainly in the hamstrings or quads.',
    ],
  ),
  gluteBridge: guide(
    'Drive through the feet and squeeze the glutes at the top without arching the lower back.',
    [
      'Lie on your back with knees bent and feet flat.',
      'Place the feet close enough that the shins approach vertical near the top.',
      'Brace the abdomen lightly before lifting.',
    ],
    [
      'Press through the feet and lift the hips by contracting the glutes.',
      'Pause at the top with ribs down and pelvis controlled.',
      'Lower slowly until the hips return to the floor.',
    ],
    [
      'Do not push the hips higher by overextending the back.',
      'Keep knees from collapsing inward.',
      'Maintain even pressure through both feet.',
    ],
  ),
  calfRaise: guide(
    'Use a full controlled ankle range: rise high, pause, then lower slowly instead of bouncing.',
    [
      'Stand securely with the balls of the feet supported and room for the heels to move.',
      'Keep the legs aligned and use a support for balance if needed.',
      'Start from a comfortable stretched heel position.',
    ],
    [
      'Press through the balls of the feet to rise as high as you can control.',
      'Pause briefly at the top.',
      'Lower slowly back into the stretch before the next repetition.',
    ],
    [
      'Do not bounce out of the bottom.',
      'Keep pressure across the big-toe and little-toe side of the forefoot.',
      'Use control rather than chasing speed.',
    ],
  ),
  plank: guide(
    'Create one straight, braced line from head through hips to heels without letting the lower back sag.',
    [
      'Place forearms or hands under the shoulders and extend the legs behind you.',
      'Set the head, ribcage, pelvis, and heels in one long line.',
      'Brace the abs and squeeze the glutes before starting the timer.',
    ],
    [
      'Hold the position while breathing normally.',
      'Push the floor away so the upper back stays supported.',
      'End the hold when you can no longer maintain the same trunk position.',
    ],
    [
      'Do not let the hips sag toward the floor.',
      'Do not pike the hips high just to extend the time.',
      'Keep the neck neutral and eyes toward the floor.',
    ],
  ),
  crunch: guide(
    'Curl the ribcage toward the pelvis using the abs; do not pull the head forward with the hands.',
    [
      'Lie on your back with knees bent and feet supported.',
      'Place fingertips lightly near the head or cross the arms over the chest.',
      'Set the neck neutral before starting.',
    ],
    [
      'Exhale as you curl the shoulder blades off the floor.',
      'Pause briefly while the abdomen is shortened.',
      'Lower under control without yanking on the neck.',
    ],
    [
      'Keep the hands light if they are behind the head.',
      'Use abdominal flexion, not momentum.',
      'A small controlled range is enough.',
    ],
  ),
  sitUp: guide(
    'Roll the torso up under control instead of throwing the arms or head forward to create momentum.',
    [
      'Lie on your back with knees bent and feet stable.',
      'Choose an arm position you can maintain without pulling on the neck.',
      'Brace lightly before beginning the upward phase.',
    ],
    [
      'Curl and raise the torso toward the thighs in a smooth motion.',
      'Reach the top without jerking the neck forward.',
      'Lower back down under control.',
    ],
    [
      'Avoid swinging the arms unless that variation is intentional.',
      'Keep the neck relaxed.',
      'Switch to a crunch or dead-bug variation if full sit-ups bother the lower back.',
    ],
  ),
  hangingLegRaise: guide(
    'Control the pelvis and avoid turning the rep into a swing; raise the legs only as high as the trunk stays stable.',
    [
      'Hang securely with shoulders active rather than completely shrugged.',
      'Brace the trunk and let the legs settle before the first rep.',
      'Use bent knees to reduce difficulty.',
    ],
    [
      'Raise the knees or legs by flexing the hips and curling the pelvis slightly.',
      'Pause at the top without swinging backward.',
      'Lower slowly until the body is still again.',
    ],
    [
      'Do not use repeated swinging for momentum.',
      'Keep shoulders engaged.',
      'Reduce the range if the lower back or hip flexors dominate.',
    ],
  ),
  rowingMachine: guide(
    'Sequence each stroke legs → body → arms on the drive, then arms → body → legs on the return.',
    [
      'Secure the feet and sit tall with the handle held lightly.',
      'At the catch, bend the knees with shins near vertical and hinge slightly forward from the hips.',
      'Keep the back neutral and shoulders relaxed.',
    ],
    [
      'Push with the legs first, then open the hips slightly, then draw the handle toward the lower ribs.',
      'Recover in reverse: extend the arms, hinge forward, then bend the knees.',
      'Use a smooth rhythm instead of rushing back to the catch.',
    ],
    [
      'Do not pull hard with the arms before the legs drive.',
      'Keep the chain/handle traveling in a mostly level line.',
      'Avoid excessive layback at the finish.',
    ],
  ),
  running: guide(
    'Run tall with relaxed shoulders and land under your body rather than reaching far out in front.',
    [
      'Start at an easy pace for several minutes before increasing intensity.',
      'Stand tall with a slight natural forward lean from the ankles.',
      'Keep the shoulders and hands relaxed.',
    ],
    [
      'Use quick, comfortable steps and let the foot land near underneath the body.',
      'Swing the arms naturally front-to-back without crossing excessively over the torso.',
      'Increase pace gradually rather than sprinting immediately from cold.',
    ],
    [
      'Avoid overstriding far in front of the hips.',
      'Keep breathing rhythmic and controlled.',
      'Slow down if form becomes heavy or unstable.',
    ],
  ),
  cycling: guide(
    'Keep the hips stable and pedal smoothly instead of rocking side-to-side or mashing an excessively heavy resistance.',
    [
      'Adjust the seat so the knee remains slightly bent at the bottom of the pedal stroke.',
      'Set handlebars so you can reach them without rounding aggressively.',
      'Begin with light resistance and an easy cadence.',
    ],
    [
      'Pedal in a smooth continuous rhythm while the knees track forward.',
      'Increase resistance or cadence gradually to reach the target effort.',
      'Keep the upper body relatively quiet while the legs do the work.',
    ],
    [
      'Do not let the knees repeatedly collapse inward.',
      'Avoid bouncing on the saddle from a cadence you cannot control.',
      'Reduce resistance if you have to rock the hips to turn the pedals.',
    ],
  ),
  elliptical: guide(
    'Stay tall and let the legs move the pedals smoothly without hanging your bodyweight on the handles.',
    [
      'Step onto the pedals securely and take a light grip on the handles.',
      'Stand tall with the trunk braced and feet flat on the pedals.',
      'Start with low resistance until the motion feels smooth.',
    ],
    [
      'Drive the pedals through a continuous oval path while keeping the hips stable.',
      'Use the moving handles naturally if desired without pulling the torso around.',
      'Adjust resistance gradually to match the planned effort.',
    ],
    [
      'Do not lean heavily onto the console or handles.',
      'Keep knees tracking with the toes.',
      'Maintain a smooth cadence rather than stomping the pedals.',
    ],
  ),
  stairClimber: guide(
    'Stand tall and take controlled steps without supporting most of your bodyweight on the handrails.',
    [
      'Step on securely and begin at a low speed.',
      'Stand upright with a light handrail touch for balance if needed.',
      'Place enough of the foot on each step to feel stable.',
    ],
    [
      'Drive through each step while keeping a steady cadence.',
      'Let the hips and knees extend naturally instead of crouching continuously.',
      'Increase speed gradually only while posture remains controlled.',
    ],
    [
      'Avoid hanging on the rails to unload the legs.',
      'Do not rush partial steps just to increase the displayed speed.',
      'Keep the torso tall.',
    ],
  ),
  burpee: guide(
    'Keep each phase controlled—squat down, plant the hands, move to plank, return the feet, then stand or jump.',
    [
      'Stand with enough clear floor space for a full plank position.',
      'Brace lightly and prepare to bend through the hips and knees.',
      'Choose a step-back version if jumping the feet is not controlled.',
    ],
    [
      'Squat down and place the hands securely on the floor.',
      'Jump or step the feet back to a strong plank, then return them underneath you.',
      'Stand tall or add the programmed jump, landing softly before the next rep.',
    ],
    [
      'Do not let the lower back sag in the plank phase.',
      'Land softly with knees tracking over toes.',
      'Slow the cadence before sacrificing position.',
    ],
  ),
  mountainClimber: guide(
    'Keep the shoulders over the hands and the hips steady while alternating the knees forward.',
    [
      'Start in a strong high-plank position with hands under shoulders.',
      'Brace the trunk and set the hips level with the torso.',
      'Spread the fingers and press firmly into the floor.',
    ],
    [
      'Drive one knee toward the chest, then switch legs.',
      'Keep the upper body as quiet as possible while the legs cycle.',
      'Choose a pace that lets the hips stay controlled.',
    ],
    [
      'Do not bounce the hips high and low.',
      'Keep shoulders stacked over the hands.',
      'Step the feet instead of running them if needed for control.',
    ],
  ),
  kettlebellSwing: guide(
    'Make the bell move from a powerful hip hinge—not a squat and not an arm raise.',
    [
      'Stand with feet around shoulder width and the kettlebell slightly in front of you.',
      'Hinge at the hips with a neutral spine and grip the bell securely.',
      'Brace the trunk and pull the shoulders down away from the ears.',
    ],
    [
      'Hike the bell back between the thighs, then drive the hips forward powerfully.',
      'Let hip extension float the bell upward while the arms stay relatively relaxed.',
      'Guide the bell back into the next hip hinge without squatting underneath it.',
    ],
    [
      'Do not lift the bell primarily with the shoulders.',
      'Keep the spine neutral as the hips move backward.',
      'Finish tall with glutes, not by leaning backward.',
    ],
  ),
  farmerCarry: guide(
    'Walk tall with the weights quiet at your sides and resist leaning or shrugging as fatigue builds.',
    [
      'Pick up the weights using a controlled hinge or squat and stand fully tall.',
      'Set the shoulders down and back lightly while keeping the ribs stacked over the pelvis.',
      'Grip firmly and choose a clear walking path.',
    ],
    [
      'Walk with short, controlled steps while keeping the torso upright.',
      'Keep the weights close to the sides without letting them swing.',
      'Set the weights down with the same controlled hinge/squat used to pick them up.',
    ],
    [
      'Do not lean away from one side.',
      'Avoid shrugging the shoulders continuously toward the ears.',
      'Shorten the distance if grip or posture fails.',
    ],
  ),
  stepUp: guide(
    'Plant the whole working foot on the box and stand by driving through that leg instead of pushing hard off the trailing foot.',
    [
      'Choose a box height you can control without twisting or launching upward.',
      'Place the entire working foot securely on the box.',
      'Keep the knee aligned with the toes.',
    ],
    [
      'Drive through the elevated foot to bring the body onto the box.',
      'Stand tall and controlled at the top.',
      'Step down deliberately rather than dropping off the box.',
    ],
    [
      'Minimize the push from the trailing leg.',
      'Keep the working knee from collapsing inward.',
      'Use a lower box if the pelvis has to twist to complete the rep.',
    ],
  ),
  boxJump: guide(
    'Jump to a height you can land on quietly with the whole foot supported, then step down under control.',
    [
      'Choose a stable box that is well within your current jumping ability.',
      'Stand close enough to reach the box without excessive forward travel.',
      'Load the hips and knees with the arms ready to assist the jump.',
    ],
    [
      'Drive through the floor and swing the arms as you jump onto the box.',
      'Land with the whole foot on the box and absorb the landing with soft hips and knees.',
      'Stand tall, then step down rather than repeatedly jumping backward off the box.',
    ],
    [
      'Prioritize a quiet, stable landing over box height.',
      'Do not use a box so high that you have to collapse into a deep crouch to make it.',
      'Stop explosive work when landing quality falls off.',
    ],
  ),
  jumpRope: guide(
    'Use small, quiet jumps and turn the rope mainly from the wrists rather than making large arm circles.',
    [
      'Stand tall with the handles around hip level and elbows close to the sides.',
      'Keep the rope behind you and the wrists relaxed.',
      'Start with low, two-foot jumps.',
    ],
    [
      'Turn the rope from the wrists and make only enough space for it to pass under the feet.',
      'Land softly on the balls/midfoot with the knees relaxed.',
      'Maintain a steady rhythm before trying to increase speed.',
    ],
    [
      'Keep the jumps low.',
      'Avoid big shoulder circles.',
      'Stop and reset rather than chasing the rope with increasingly high jumps.',
    ],
  ),
};

const includesAny = (name, terms) => terms.some((term) => name.includes(term));
const exactAny = (name, terms) => terms.includes(name);

export const getExerciseFormData = (exerciseName, muscleGroup = '') => {
  const name = normalizeExerciseName(exerciseName || '');
  const muscle = normalizeExerciseName(muscleGroup || '');
  const alternativeGuide = getExerciseAlternativeFormGuide(exerciseName);

  if (alternativeGuide) return alternativeGuide;

  // Specific and exact-safe rules first.
  //
  // IMPORTANT:
  // A shared word such as "push up", "squat", "curl", or "press" is not
  // sufficient evidence that two exercise variations use the same setup.
  // Unknown or specialty variations deliberately fall through to FALLBACK.

  if (exactAny(name, ['dumbbell floor press', 'floor press'])) {
    return GUIDES.floorPress;
  }

  if (exactAny(name, [
    'bench press',
    'barbell bench press',
    'dumbbell bench press',
    'dumbbell chest press',
    'incline dumbbell press',
    'incline barbell bench press',
    'decline barbell bench press',
    'close grip bench press',
    'neutral grip dumbbell press',
  ])) {
    return GUIDES.benchPress;
  }

  if (exactAny(name, [
    'chest fly',
    'dumbbell chest fly',
    'dumbbell fly',
  ])) {
    return GUIDES.chestFly;
  }

  if (name === 'bench dip') return GUIDES.benchDip;

  if (exactAny(name, [
    'overhead tricep extension',
    'overhead triceps extension',
    'dumbbell overhead tricep extension',
    'dumbbell overhead triceps extension',
  ])) {
    return GUIDES.overheadTricepsExtension;
  }

  if (includesAny(name, ['tricep kickback', 'triceps kickback'])) {
    return GUIDES.tricepsKickback;
  }

  if (includesAny(name, [
    'tricep pushdown',
    'triceps pushdown',
    'cable pushdown',
  ])) {
    return GUIDES.tricepsPushdown;
  }

  if (exactAny(name, [
    'romanian deadlift',
    'dumbbell romanian deadlift',
    'stiff leg deadlift',
    'stiff legged deadlift',
    'dumbbell stiff leg deadlift',
  ]) || name === 'rdl') {
    return GUIDES.romanianDeadlift;
  }

  if (includesAny(name, ['rowing machine', 'rower'])) {
    return GUIDES.rowingMachine;
  }

  if (exactAny(name, [
    'seated row',
    'seated cable row',
    'seated row machine',
  ])) {
    return GUIDES.seatedRow;
  }

  if (exactAny(name, [
    'barbell bent over row',
    'dumbbell bent over row',
    'bent over rowing',
    'bent over rowing reverse',
    'barbell row',
    'barbell row overhand',
    'barbell row underhand',
  ])) {
    return GUIDES.bentOverRow;
  }

  if (includesAny(name, ['lat pulldown', 'lat pull down'])) {
    return GUIDES.latPulldown;
  }

  if (includesAny(name, ['face pull'])) {
    return GUIDES.facePull;
  }

  if (name === 'chin up') {
    return GUIDES.chinUp;
  }

  if (exactAny(name, [
    'pull up',
    'weighted pull up',
  ])) {
    return GUIDES.pullUp;
  }

  // Exact variant guides above are checked before the base movement.
  if (name === 'push up') {
    return GUIDES.pushUp;
  }

  if (name === 'jumping jack') {
    return GUIDES.jumpingJack;
  }

  if (exactAny(name, [
    'overhead press',
    'seated dumbbell shoulder press',
    'machine shoulder press',
    'shoulder press dumbbell',
    'shoulder press barbell',
    'military press',
  ])) {
    return GUIDES.overheadPress;
  }

  if (includesAny(name, ['lateral raise', 'side raise'])) {
    return GUIDES.lateralRaise;
  }

  if (includesAny(name, ['front raise'])) {
    return GUIDES.frontRaise;
  }

  if (includesAny(name, ['hammer curl'])) {
    return GUIDES.hammerCurl;
  }

  // Hamstring curls MUST resolve before generic arm-curl rules.
  if (exactAny(name, [
    'leg curl',
    'lying leg curl',
    'seated leg curl',
    'standing leg curl',
    'hamstring curl',
  ])) {
    return GUIDES.legCurl;
  }

  const bicepsCurlCandidate =
    includesAny(name, [
      'bicep curl',
      'biceps curl',
      'dumbbell curl',
      'barbell curl',
      'ez bar curl',
      'preacher curl',
      'cable curl',
      'concentration curl',
      'band curl',
      'kettlebell curl',
      'reverse curl',
      'spider curl',
      'zottman curl',
      'bayesian curl',
    ]) &&
    !includesAny(name, [
      'wrist curl',
      'nordic curl',
      'leg curl',
      'hamstring curl',
      'curl to press',
    ]);

  if (bicepsCurlCandidate) {
    return GUIDES.bicepsCurl;
  }

  if (
    name === 'dip' ||
    includesAny(name, [
      'tricep dip',
      'triceps dip',
      'parallel bar dip',
    ])
  ) {
    return GUIDES.dip;
  }

  if (exactAny(name, [
    'deadlift',
    'barbell deadlift',
    'dumbbell deadlift',
  ])) {
    return GUIDES.deadlift;
  }

  if (exactAny(name, [
    'leg press',
    'leg presses narrow',
    'leg presses wide',
    'leg press on hackenschmidt machine',
  ])) {
    return GUIDES.legPress;
  }

  if (exactAny(name, [
    'leg extension',
    'single leg extension',
  ])) {
    return GUIDES.legExtension;
  }

  if (exactAny(name, [
    'hip thrust',
    'barbell hip thrust',
    'dumbbell hip thrust',
  ])) {
    return GUIDES.hipThrust;
  }

  if (exactAny(name, [
    'glute bridge',
    'glute bridges',
    'hip bridge',
  ])) {
    return GUIDES.gluteBridge;
  }

  if (
    includesAny(name, ['calf raise']) &&
    !includesAny(name, ['seated', 'sitting'])
  ) {
    return GUIDES.calfRaise;
  }

  if (exactAny(name, [
    'squat',
    'bodyweight squat',
    'goblet squat',
    'dumbbell goblet squat',
    'barbell squat',
    'barbell back squat',
    'front squat',
    'dumbbell front squat',
    'box squat',
    'resistance band squat',
    'prisoner squat',
    'braced squat',
    'pin squat',
  ])) {
    return GUIDES.squat;
  }

  if (exactAny(name, [
    'lunge',
    'walking lunge',
    'reverse lunge',
    'forward lunge',
    'barbell lunge standing',
    'barbell lunge walking',
    'dumbbell lunge standing',
    'dumbbell lunge walking',
    'dumbbell rear lunge',
    'alternate back lunge',
    'bodyweight lunge hd',
  ])) {
    return GUIDES.lunge;
  }

  if (includesAny(name, [
    'hanging leg raise',
    'hanging knee raise',
  ])) {
    return GUIDES.hangingLegRaise;
  }

  if (exactAny(name, [
    'plank',
    'forearm plank',
    'high plank',
  ])) {
    return GUIDES.plank;
  }

  if (name === 'sit up') {
    return GUIDES.sitUp;
  }

  if (name === 'crunch') {
    return GUIDES.crunch;
  }

  if (includesAny(name, ['burpee'])) {
    return GUIDES.burpee;
  }

  if (includesAny(name, ['mountain climber'])) {
    return GUIDES.mountainClimber;
  }

  if (includesAny(name, ['kettlebell swing'])) {
    return GUIDES.kettlebellSwing;
  }

  if (includesAny(name, [
    'farmer carry',
    'farmers carry',
    'farmer walk',
  ])) {
    return GUIDES.farmerCarry;
  }

  if (includesAny(name, ['box jump'])) {
    return GUIDES.boxJump;
  }

  if (includesAny(name, ['step up'])) {
    return GUIDES.stepUp;
  }

  if (includesAny(name, [
    'jump rope',
    'rope skipping',
    'skipping rope',
  ])) {
    return GUIDES.jumpRope;
  }

  if (includesAny(name, [
    'stair climber',
    'stair stepper',
  ])) {
    return GUIDES.stairClimber;
  }

  if (includesAny(name, ['elliptical'])) {
    return GUIDES.elliptical;
  }

  // Do not treat the Pilates/core exercise named "Bicycle" as cycling.
  if (includesAny(name, [
    'stationary bike',
    'exercise bike',
    'cycling',
  ])) {
    return GUIDES.cycling;
  }

  if (includesAny(name, [
    'treadmill',
    'running',
    'jogging',
  ])) {
    return GUIDES.running;
  }

  // Unknown movements deliberately use the neutral internal fallback.
  // Muscle-group metadata is not used to invent exercise-specific coaching.
  void muscle;

  return FALLBACK;
};
